import { DetailsPopup } from '../table-session/DetailsPopup';
import { SEAT_NAMES, isSeat } from '../table-session/SeatLabel';
import { editorNameWithDate } from '../../utils/editorName';
import type { ConceptualProblem } from '../../types/conceptualProblems';

/**
 * The phone expansion of the masthead's compact row (feature 029): everything the status line
 * spells out on desktop, reached from the ⓘ.
 *
 * <p>Below `md` the status line is `display:none` and therefore out of the accessibility tree
 * altogether, so this popup is the ONLY place a phone — screen reader or not — can reach the
 * problem's scoring, difficulty, tags and source. It is not a convenience.</p>
 *
 * <p>It uses the shared popup shell with its own `testId`: the frozen `details-popup` belongs to
 * the practice pages' session popup, and a selector that matched both would be ambiguous the
 * moment the two surfaces are asserted by the same helper.</p>
 */
export function ConceptualDetailsPopup({
  problem,
  position,
  total,
  movedOut = false,
  onClose,
}: {
  problem: ConceptualProblem;
  position: number;
  total: number;
  /** As on the status line — the problem has just been re-filed out of this category (FR-032). */
  movedOut?: boolean;
  onClose: () => void;
}) {
  return (
    <DetailsPopup
      title="Problem Details"
      ariaLabel="Problem details"
      onClose={onClose}
      testId="conceptual-details-popup"
    >
      <div>
        <span className="text-ink-soft">Serial:</span>{' '}
        <span className="font-medium">{problem.serial}</span>
      </div>
      <div>
        <span className="text-ink-soft">Problem:</span>{' '}
        <span className="font-medium">
          {position} of {total}
        </span>
      </div>
      <div>
        <span className="text-ink-soft">Scoring:</span>{' '}
        <span className="font-medium">{problem.scoring || 'N/A'}</span>
      </div>
      <div>
        <span className="text-ink-soft">Difficulty:</span>{' '}
        <span className="font-medium">
          {problem.difficultyLevel != null ? `${problem.difficultyLevel}/10` : 'N/A'}
        </span>
      </div>
      {isSeat(problem.decisionSeat) && (
        <div>
          <span className="text-ink-soft">Asked of:</span>{' '}
          <span className="font-medium">{SEAT_NAMES[problem.decisionSeat]}</span>
        </div>
      )}
      {problem.source && (
        <div>
          <span className="text-ink-soft">From:</span>{' '}
          <span className="font-medium">{problem.source}</span>
        </div>
      )}
      {movedOut && (
        <div className="italic" data-testid="conceptual-details-moved-out">
          No longer in this category.
        </div>
      )}
      {/* On a phone this popup is the ONLY place the categories can be read: the status line that
          carries them is display:none below md, and therefore out of the accessibility tree too.
          Shown identically whether proposed at import or chosen by an editor (FR-033, FR-037). */}
      <div data-testid="conceptual-details-categories">
        <span className="text-ink-soft">Categories:</span>{' '}
        <span className="font-medium">
          {problem.categories.length > 0
            ? problem.categories.map((c) => c.name).join(', ')
            : 'Uncategorised'}
        </span>
      </div>
      {/* The full name and the date behind the masthead pill's "Jane D." (FR-047). The date lives
          here and in the pills' tooltips, never on the main screen. The pill is aria-hidden, so for
          a screen reader on a phone this is where the name is read. */}
      {problem.lastEdit && (
        <div data-testid="conceptual-details-last-editor">
          <span className="text-ink-soft">Last edited by:</span>{' '}
          <span className="font-medium">{editorNameWithDate(problem.lastEdit)}</span>
        </div>
      )}
      {problem.locked && (
        <div data-testid="conceptual-details-locked">
          <span className="text-ink-soft">Locked:</span>{' '}
          <span className="font-medium">by a Super Editor — only a Super Editor can change its categories</span>
        </div>
      )}
      {problem.components.length > 0 && (
        <div data-testid="conceptual-details-tags">
          {/* Wording exactly as recorded — the same rule the status line's pills follow. */}
          <span className="text-ink-soft">Tags:</span>{' '}
          <span className="font-medium">{problem.components.join(' · ')}</span>
        </div>
      )}
    </DetailsPopup>
  );
}
